import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Zap, Timer, Trophy, RotateCcw, Play, Pause, RotateCw } from 'lucide-react';

const TetrisGame = () => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [time, setTime] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [level, setLevel] = useState(1);
  const [lines, setLines] = useState(0);
  
  const gameLoopRef = useRef(null);
  
  // Game state
  const [board, setBoard] = useState(Array(20).fill().map(() => Array(10).fill(0)));
  const [currentPiece, setCurrentPiece] = useState(null);
  const [currentPosition, setCurrentPosition] = useState({ x: 0, y: 0 });
  const [gameSpeed, setGameSpeed] = useState(1000);
  
  const boardWidth = 10;
  const boardHeight = 20;

  // Tetromino shapes
  const tetrominoes = {
    I: [
      [1, 1, 1, 1]
    ],
    O: [
      [1, 1],
      [1, 1]
    ],
    T: [
      [0, 1, 0],
      [1, 1, 1]
    ],
    S: [
      [0, 1, 1],
      [1, 1, 0]
    ],
    Z: [
      [1, 1, 0],
      [0, 1, 1]
    ],
    J: [
      [1, 0, 0],
      [1, 1, 1]
    ],
    L: [
      [0, 0, 1],
      [1, 1, 1]
    ]
  };

  const colors = {
    I: 'bg-cyan-400',
    O: 'bg-yellow-400',
    T: 'bg-purple-400',
    S: 'bg-green-400',
    Z: 'bg-red-400',
    J: 'bg-blue-400',
    L: 'bg-orange-400'
  };

  const startGame = () => {
    setIsPlaying(true);
    setGameOver(false);
    setScore(0);
    setTime(0);
    setLevel(1);
    setLines(0);
    setBoard(Array(20).fill().map(() => Array(10).fill(0)));
    setGameSpeed(1000);
    setIsPaused(false);
    spawnNewPiece();
  };

  const pauseGame = () => {
    setIsPaused(!isPaused);
  };

  const resetGame = () => {
    setIsPlaying(false);
    setGameOver(false);
    setScore(0);
    setTime(0);
    setLevel(1);
    setLines(0);
    setBoard(Array(20).fill().map(() => Array(10).fill(0)));
    setGameSpeed(1000);
    setIsPaused(false);
    setCurrentPiece(null);
    setCurrentPosition({ x: 0, y: 0 });
  };

  const spawnNewPiece = () => {
    const pieces = Object.keys(tetrominoes);
    const randomPiece = pieces[Math.floor(Math.random() * pieces.length)];
    setCurrentPiece(randomPiece);
    setCurrentPosition({ x: Math.floor(boardWidth / 2) - 1, y: 0 });
  };

  const isValidMove = (piece, position) => {
    const shape = tetrominoes[piece];
    for (let y = 0; y < shape.length; y++) {
      for (let x = 0; x < shape[y].length; x++) {
        if (shape[y][x]) {
          const newX = position.x + x;
          const newY = position.y + y;
          if (newX < 0 || newX >= boardWidth || newY >= boardHeight) {
            return false;
          }
          if (newY >= 0 && board[newY][newX]) {
            return false;
          }
        }
      }
    }
    return true;
  };

  const placePiece = () => {
    if (!currentPiece) return;
    
    const shape = tetrominoes[currentPiece];
    const newBoard = [...board];
    
    for (let y = 0; y < shape.length; y++) {
      for (let x = 0; x < shape[y].length; x++) {
        if (shape[y][x]) {
          const boardY = currentPosition.y + y;
          const boardX = currentPosition.x + x;
          if (boardY >= 0) {
            newBoard[boardY][boardX] = currentPiece;
          }
        }
      }
    }
    
    setBoard(newBoard);
    checkLines(newBoard);
    spawnNewPiece();
    
    // Check if game over
    if (!isValidMove(currentPiece, { x: Math.floor(boardWidth / 2) - 1, y: 0 })) {
      setGameOver(true);
      setIsPlaying(false);
      if (score > highScore) {
        setHighScore(score);
      }
    }
  };

  const checkLines = (boardState) => {
    let linesCleared = 0;
    const newBoard = boardState.filter(row => {
      if (row.every(cell => cell !== 0)) {
        linesCleared++;
        return false;
      }
      return true;
    });
    
    if (linesCleared > 0) {
      const newLines = lines + linesCleared;
      setLines(newLines);
      setScore(prev => prev + linesCleared * 100 * level);
      setLevel(Math.floor(newLines / 10) + 1);
      setGameSpeed(Math.max(100, 1000 - (level - 1) * 100));
      
      // Add empty rows at top
      while (newBoard.length < boardHeight) {
        newBoard.unshift(Array(boardWidth).fill(0));
      }
      setBoard(newBoard);
    }
  };

  const movePiece = (dx, dy) => {
    if (!currentPiece || !isPlaying || gameOver || isPaused) return;
    
    const newPosition = { x: currentPosition.x + dx, y: currentPosition.y + dy };
    if (isValidMove(currentPiece, newPosition)) {
      setCurrentPosition(newPosition);
    } else if (dy > 0) {
      placePiece();
    }
  };

  const rotatePiece = () => {
    if (!currentPiece || !isPlaying || gameOver || isPaused) return;
    
    const shape = tetrominoes[currentPiece];
    const rotated = shape[0].map((_, i) => shape.map(row => row[i]).reverse());
    
    if (isValidMove(currentPiece, currentPosition)) {
      // Temporarily replace the piece to check rotation
      const tempTetrominoes = { ...tetrominoes, [currentPiece]: rotated };
      if (isValidMove(currentPiece, currentPosition)) {
        setTetrominoes(prev => ({ ...prev, [currentPiece]: rotated }));
      }
    }
  };

  const gameLoop = useCallback(() => {
    if (!isPlaying || gameOver || isPaused) return;
    movePiece(0, 1);
  }, [isPlaying, gameOver, isPaused]);

  useEffect(() => {
    if (isPlaying && !gameOver && !isPaused) {
      gameLoopRef.current = setInterval(gameLoop, gameSpeed);
    }
    return () => {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
    };
  }, [isPlaying, gameOver, isPaused, gameLoop, gameSpeed]);

  useEffect(() => {
    let interval;
    if (isPlaying && !gameOver && !isPaused) {
      interval = setInterval(() => {
        setTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, gameOver, isPaused]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isPlaying || gameOver) return;

      switch (e.code) {
        case 'ArrowLeft':
          e.preventDefault();
          movePiece(-1, 0);
          break;
        case 'ArrowRight':
          e.preventDefault();
          movePiece(1, 0);
          break;
        case 'ArrowDown':
          e.preventDefault();
          movePiece(0, 1);
          break;
        case 'ArrowUp':
        case 'KeyZ':
          e.preventDefault();
          rotatePiece();
          break;
        case 'Space':
          e.preventDefault();
          if (!isPlaying) {
            startGame();
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isPlaying, gameOver]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Game Header */}
      <div className="cyber-card p-6 mb-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
          <div className="flex items-center justify-center space-x-2">
            <Zap className="h-6 w-6 text-neon-blue" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.score')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-blue">{score}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Trophy className="h-6 w-6 text-neon-green" />
            <div>
              <p className="text-sm text-gray-400 font-tech">High Score</p>
              <p className="text-2xl font-cyber font-bold text-neon-green">{highScore}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Timer className="h-6 w-6 text-neon-pink" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.time')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-pink">{formatTime(time)}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <RotateCw className="h-6 w-6 text-neon-purple" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.level')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-purple">{level}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <RotateCcw className="h-6 w-6 text-neon-yellow" />
            <button
              onClick={resetGame}
              className="cyber-button text-sm px-4 py-2"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Game Instructions */}
      {!isPlaying && !gameOver && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="cyber-card p-6 mb-8 text-center"
        >
          <h3 className="text-2xl font-cyber font-bold text-white mb-4">
            Cyber Tetris
          </h3>
          <p className="text-gray-300 font-tech mb-6">
            Use arrow keys to move and rotate pieces. Clear lines to score points and level up!
          </p>
          <button
            onClick={startGame}
            className="cyber-button text-lg px-8 py-3"
          >
            Start Game
          </button>
        </motion.div>
      )}

      {/* Game Over Message */}
      {gameOver && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="cyber-card p-6 mb-8 text-center border-neon-red/50"
        >
          <h3 className="text-3xl font-cyber font-bold text-neon-red mb-4">
            Game Over!
          </h3>
          <p className="text-gray-300 font-tech mb-4">
            Score: {score} | Level: {level} | Lines: {lines} | Time: {formatTime(time)}
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={startGame}
              className="cyber-button text-lg px-6 py-3"
            >
              Play Again
            </button>
            <button
              onClick={resetGame}
              className="cyber-button text-lg px-6 py-3 border-neon-red text-neon-red hover:bg-neon-red hover:text-dark-300"
            >
              Reset
            </button>
          </div>
        </motion.div>
      )}

      {/* Game Canvas */}
      <div className="cyber-card p-6">
        <div className="relative">
          {/* Pause Button */}
          {isPlaying && !gameOver && (
            <button
              onClick={pauseGame}
              className="absolute top-4 right-4 z-10 cyber-button text-sm px-3 py-2"
            >
              {isPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
            </button>
          )}
          
          {/* Game Board */}
          <div 
            className="relative w-full h-80 bg-dark-100 border-2 border-neon-blue/30 rounded-lg overflow-hidden"
            style={{ background: 'linear-gradient(to bottom, #1a1a2e 0%, #16213e 100%)' }}
          >
            {/* Grid Lines */}
            <div className="absolute inset-0 opacity-10">
              {Array.from({ length: boardHeight + 1 }).map((_, i) => (
                <div 
                  key={i}
                  className="absolute bg-neon-blue/30"
                  style={{
                    top: `${(i * 16)}px`,
                    left: 0,
                    height: '1px',
                    width: '100%'
                  }}
                />
              ))}
              {Array.from({ length: boardWidth + 1 }).map((_, i) => (
                <div 
                  key={i}
                  className="absolute bg-neon-blue/30"
                  style={{
                    left: `${(i * 16)}px`,
                    top: 0,
                    width: '1px',
                    height: '100%'
                  }}
                />
              ))}
            </div>
            
            {/* Placed Pieces */}
            {board.map((row, y) =>
              row.map((cell, x) =>
                cell ? (
                  <div
                    key={`${x}-${y}`}
                    className={`absolute ${colors[cell]} rounded-sm`}
                    style={{
                      left: `${x * 16}px`,
                      top: `${y * 16}px`,
                      width: '16px',
                      height: '16px'
                    }}
                  />
                ) : null
              )
            )}
            
            {/* Current Piece */}
            {currentPiece && (
              tetrominoes[currentPiece].map((row, y) =>
                row.map((cell, x) =>
                  cell ? (
                    <div
                      key={`current-${x}-${y}`}
                      className={`absolute ${colors[currentPiece]} rounded-sm border border-white/50`}
                      style={{
                        left: `${(currentPosition.x + x) * 16}px`,
                        top: `${(currentPosition.y + y) * 16}px`,
                        width: '16px',
                        height: '16px'
                      }}
                    />
                  ) : null
                )
              )
            )}
            
            {/* Score Display */}
            <div className="absolute top-4 left-4 text-neon-blue font-cyber font-bold text-2xl">
              {score}
            </div>
          </div>
        </div>
      </div>

      {/* Controls Info */}
      <div className="cyber-card p-6 mt-8">
        <div className="text-center">
          <h4 className="text-xl font-cyber font-bold text-white mb-4">
            Controls
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-gray-300 font-tech">
            <div>
              <p className="font-bold text-neon-blue">← →</p>
              <p>Move Left/Right</p>
            </div>
            <div>
              <p className="font-bold text-neon-green">↓</p>
              <p>Move Down</p>
            </div>
            <div>
              <p className="font-bold text-neon-pink">↑ / Z</p>
              <p>Rotate Piece</p>
            </div>
            <div>
              <p className="font-bold text-neon-yellow">SPACE</p>
              <p>Start Game</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TetrisGame;
